import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';

const AgentAuthContext = createContext(null);

export const AgentAuthProvider = ({ children }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Load agent and token from localStorage on mount
    const storedToken = localStorage.getItem('treatx_agent_token');
    const storedAgent = localStorage.getItem('treatx_agent');
    
    if (storedToken && storedAgent) {
      try {
        setToken(storedToken);
        setAgent(JSON.parse(storedAgent));
        // Set default authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } catch (e) {
        localStorage.removeItem('treatx_agent_token');
        localStorage.removeItem('treatx_agent');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/crm/auth/agent/login', { email, password });
      
      if (response.data.success) {
        const { token, agent } = response.data;
        setToken(token);
        setAgent(agent);
        localStorage.setItem('treatx_agent_token', token);
        localStorage.setItem('treatx_agent', JSON.stringify(agent));
        // Set default authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return { success: true };
      }
      return { success: false, error: 'Login failed' };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    setAgent(null);
    setToken(null);
    localStorage.removeItem('treatx_agent_token');
    localStorage.removeItem('treatx_agent');
    delete api.defaults.headers.common['Authorization'];
  };

  const updateAgent = (updates) => {
    const updatedAgent = { ...agent, ...updates };
    setAgent(updatedAgent);
    localStorage.setItem('treatx_agent', JSON.stringify(updatedAgent));
  };

  // P0-AUTH-IDENTITY-SWEEP-01 — the agent bearer is installed as a DEFAULT
  // header on the SHARED axios client, and this provider wraps the whole
  // app. A TreatX logout must therefore drop it here too, otherwise the
  // previous agent identity keeps riding on every request made by whoever
  // uses the tab next (until a full reload). Both canonical logout paths
  // dispatch `treatx:identity-changed`.
  useEffect(() => {
    const onIdentityChanged = () => {
      setAgent(null);
      setToken(null);
      try {
        localStorage.removeItem('treatx_agent_token');
        localStorage.removeItem('treatx_agent');
      } catch { /* storage disabled — non-fatal */ }
      delete api.defaults.headers.common['Authorization'];
    };
    window.addEventListener('treatx:identity-changed', onIdentityChanged);
    return () => window.removeEventListener('treatx:identity-changed', onIdentityChanged);
  }, []);

  return (
    <AgentAuthContext.Provider value={{ agent, token, login, logout, updateAgent, loading }}>
      {children}
    </AgentAuthContext.Provider>
  );
};

export const useAgentAuth = () => {
  const context = useContext(AgentAuthContext);
  if (!context) {
    throw new Error('useAgentAuth must be used within AgentAuthProvider');
  }
  return context;
};
